import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthenticationV2Service } from '@brand-lolly/app/authentication-v2/services/authentication-v2.service';
import { SIGNUP_SOURCE_SOCIAL_LISTENING } from '@brand-lolly/app/authentication-v2/models/auth.model';
import {
	funnelForOrigin,
	OnboardingFunnelKey
} from '@brand-lolly/app/onboarding/onboarding-funnel.catalogue';
import { OnboardingService } from './onboarding.service';

// Re-exported so guards/components keep a single import site for the resolver
// surface; the canonical definition lives in auth.model (DI-cycle-free).
export { SIGNUP_SOURCE_SOCIAL_LISTENING };

/** Where a brand belongs right now, and why. */
export interface OnboardingEntryDecision {
	/** True while the brand still owes onboarding. */
	owesOnboarding: boolean;
	/** The funnel its origin puts it in — answered even when it owes nothing. */
	funnel: OnboardingFunnelKey;
	/** Where to send it: the funnel's route, or the application root. */
	route: string;
	/**
	 * True when the recorded progress could not be READ, as opposed to read as
	 * unfinished. A surface that would otherwise cancel a navigation should let
	 * it through on this: "we could not find out" is not "you may not proceed".
	 */
	progressUnknown: boolean;
}

/**
 * The single onboarding-routing decision. Every entry point that used to
 * hardcode a funnel route defers here, so one place owns the choice and the
 * shell guard, the funnel guards and the post-sign-in redirect cannot disagree
 * about where one brand belongs.
 *
 * TWO SEPARATE QUESTIONS, ANSWERED IN THIS ORDER:
 *
 * 1. *Does this brand still owe onboarding?* — recorded onboarding progress,
 *    and nothing else. `isOnboardingRequired()` reads the brand's
 *    `current_onboarding_step`, which is the same fact the API's onboarding
 *    gate reads and the fact every funnel writes when it completes. It is
 *    deliberately NOT a billing question: what a brand has bought says nothing
 *    about what it has finished. This branch previously answered it with
 *    `hasProductAccess(SOCIAL_LISTENING)`, which was true while Social
 *    Listening was a purchasable per-product subscription and became wrong the
 *    day the unified catalogue made it a Business-only tier feature — from then
 *    on every Free and Pro brand read as "not onboarded" forever.
 * 2. *Which funnel does it belong to?* — the recorded acquisition origin
 *    (`signup_source`), mapped by the funnel catalogue. Attribution alone; no
 *    second field for intent, and no screen assembling a funnel of its own.
 *
 * CONTRACT: nothing SENDS a `signup_source` yet (T-2066), so the claim is
 * absent for almost every brand and the catalogue answers with the default
 * funnel — which is what every brand got before this existed.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingEntryResolverService {
	constructor(
		private authService: AuthenticationV2Service,
		private onboardingService: OnboardingService
	) {}

	resolveOnboardingEntry(): Observable<OnboardingEntryDecision> {
		const origin = this.authService.getCurrentDecodedToken()?.signup_source;
		const entry = funnelForOrigin(origin);

		return this.onboardingService.isOnboardingRequired().pipe(
			map(required => ({
				owesOnboarding: required,
				funnel: entry.key,
				route: required ? entry.route : '/',
				progressUnknown: false
			})),
			// Unreadable progress resumes the funnel rather than admitting a
			// brand the API gate may still refuse — but it says so, so a caller
			// that must not cancel a navigation on a failed read can tell.
			catchError(() =>
				of({
					owesOnboarding: true,
					funnel: entry.key,
					route: entry.route,
					progressUnknown: true
				})
			)
		);
	}
}
